import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useLocalization } from "@/context/localization-context";
import type { GoogleSignInProvider } from "@/api/sign-in";
import { loadGoogleIdentity } from "@/lib/google-identity";

// Google Identity Services keeps one configuration per page: initialise it once per client and route its
// callback to the button that is mounted now.
let initializedClientId: string | null = null;
let credentialHandler: ((credential: string) => void) | null = null;

/** "Sign in with Google" for the company Workspace, drawn by Google into the div (follows the UI language). */
export function GoogleSignInButton({
  provider,
  onCredential,
  disabled,
}: {
  provider: GoogleSignInProvider;
  onCredential: (credential: string) => void;
  disabled?: boolean;
}) {
  const { locale, t } = useLocalization();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    credentialHandler = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleIdentity()
      .then((gis) => {
        const container = containerRef.current;
        if (cancelled || !container) {
          return;
        }
        if (initializedClientId !== provider.client_id) {
          gis.initialize({
            client_id: provider.client_id,
            hd: provider.domain,
            ux_mode: "popup",
            auto_select: false,
            callback: (response) => credentialHandler?.(response.credential),
          });
          initializedClientId = provider.client_id;
        }
        container.replaceChildren();
        gis.renderButton(container, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          logo_alignment: "left",
          width: Math.round(Math.min(400, Math.max(200, container.clientWidth))),
          locale,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [provider.client_id, provider.domain, locale]);

  if (loadFailed) {
    return <p className="text-center text-sm text-muted-foreground">{t("login.google.loadFailed")}</p>;
  }

  return (
    <div
      ref={containerRef}
      aria-busy={disabled}
      // Google's button iframe is a light page: inside the dark page (color-scheme: dark) the browser would
      // paint a white backdrop behind it
      style={{ colorScheme: "light" }}
      className={cn("flex min-h-10 w-full justify-center", disabled && "pointer-events-none opacity-50")}
    />
  );
}
